import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  IconButton,
  FormControl,
  Box,
  Autocomplete,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useAdd from "../../../../api/useAdd";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import { useAuth } from "../../../../context";
import Type from "./Type";

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const AddProcedure = ({ onClose, data = null }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  // Patient search state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [patientInputValue, setPatientInputValue] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0);

  const [open, setOpen] = useState(null);
  const [type, setType] = useState("");
  const [formData, setFormData] = useState({
    type: "",
    procedure_date: "",
    procedure_day: "",
    status: "",
    period: "",
    doctor: "",
    lab: [],
    total_cost: "",
    discount: "",
    advance: "",
    advance_currency: "",
    exchange_rate: "",
    currency: "",
    zone: "",
    description: "",
    next_visit_days: "",
    next_visit_date: "",
    patient: null,
  });

  const [formErrors, setFormErrors] = useState({});
  const [images, setImages] = useState([]);
  const [imageErrors, setImageErrors] = useState([]);

  const {
    data: users,
    isLoading: isLoadingUser,
    isError: isErrorUser,
  } = useFetchObjects(["users"], "users/?position=7", token);

  const {
    data: typesData,
    isLoading: isLoadingType,
    isError: isErrorType,
  } = useFetchObjects(["procedure-type"], `procedure-type/?type=2`, token);

  const {
    data: patientsData,
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
    refetch: refetchPatients,
  } = useFetchObjects(
    ["patients", page, pageSize, searchTerm, forceUpdate],
    `patients/?page=${page}&page_size=${pageSize}&search=${searchTerm}`,
    token
  );

  const {
    data: periods,
    isLoading: isLoadingPeriod,
    isError: isErrorPeriod,
  } = useFetchObjects(["period"], "period", token);

  const {
    data: labs,
    isLoading: isLoadingLab,
    isError: isErrorLab,
  } = useFetchObjects(["lab"], "lab", token);

  const { handleAdd, loading, resetForm } = useAdd("procedure", token);
  const { handleDelete, ConfirmDialog } = useDelete("procedure-type", token);
  const {
    handleDelete: handleDeletePeriod,
    ConfirmDialog: PeriodConfirmDialog,
  } = useDelete("period", token);
  const { handleDelete: handleDeleteLab, ConfirmDialog: LabConfirmDialog } =
    useDelete("lab", token);

  // Patient data handling
  useEffect(() => {
    if (patientsData?.results) {
      setAllPatients((prevPatients) => {
        const newPatients = [...prevPatients, ...patientsData.results];
        const uniquePatients = newPatients.reduce((acc, current) => {
          if (!acc.find((item) => item.id === current.id)) acc.push(current);
          return acc;
        }, []);
        return uniquePatients;
      });
      setHasMore(patientsData.next !== null);
    } else {
      setHasMore(false);
    }
  }, [patientsData]);

  useEffect(() => {
    if (!searchTerm) {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetchPatients();
    }
  }, []);

  useEffect(() => {
    if (searchTerm !== "") {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetchPatients();
    } else {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetchPatients();
    }
  }, [searchTerm, refetchPatients]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Auto-calculate next_visit_date
  useEffect(() => {
    if (formData.next_visit_days) {
      const daysToAdd = parseInt(formData.next_visit_days, 10);
      if (!isNaN(daysToAdd)) {
        const baseDate = formData.procedure_date
          ? new Date(formData.procedure_date)
          : new Date();
        baseDate.setDate(baseDate.getDate() + daysToAdd);
        const year = baseDate.getFullYear();
        const month = ("0" + (baseDate.getMonth() + 1)).slice(-2);
        const day = ("0" + baseDate.getDate()).slice(-2);
        const formattedDate = `${year}-${month}-${day}`;
        setFormData((prev) => ({
          ...prev,
          next_visit_date: formattedDate,
        }));
      }
    }
  }, [formData.procedure_date, formData.next_visit_days]);

  useEffect(() => {
    if (resetForm) {
      setFormData({
        type: "",
        procedure_date: "",
        procedure_day: "",
        status: "",
        period: "",
        doctor: "",
        lab: [],
        total_cost: "",
        discount: "",
        advance: "",
        advance_currency: "",
        exchange_rate: "",
        currency: "",
        zone: "",
        description: "",
        next_visit_days: "",
        next_visit_date: "",
        patient: null,
      });
      setPatientInputValue("");
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      navigate("/beauty/view-procedure");
    }
  }, [resetForm, navigate]);

  // Patient Autocomplete Handlers
  const handlePatientInputChange = (event, newInputValue) => {
    setPatientInputValue(newInputValue);
    debouncedSetSearchTerm(newInputValue);
    setFormErrors((prev) => ({ ...prev, patient: "" }));
  };

  const handlePatientChange = (event, newValue) => {
    setFormData((prev) => ({ ...prev, patient: newValue }));
    setFormErrors((prev) => ({ ...prev, patient: "" }));
  };

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMore &&
      !isLoadingPatient
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  // Debounced setSearchTerm
  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  const handleImageChange = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const updatedImages = [...images];
      updatedImages[index] = file;
      setImages(updatedImages);
      const newImageErrors = [...imageErrors];
      newImageErrors[index] = "";
      setImageErrors(newImageErrors);
    }
  };

  const addImageField = () => {
    setImages([...images, null]);
  };

  const removeImageField = (index) => {
    const updatedImages = [...images];
    updatedImages.splice(index, 1);
    setImages(updatedImages);

    const newImageErrors = [...imageErrors];
    newImageErrors.splice(index, 1);
    setImageErrors(newImageErrors);
  };

  const validateImages = () => {
    let isValid = true;
    const errors = [];
    images.forEach((image, index) => {
      if (!image) {
        errors[index] = t("call_center.imageRequired") || "Image is required.";
        isValid = false;
      } else if (image.size > 2 * 1024 * 1024) {
        errors[index] =
          t("call_center.imageTooLarge") || "Image size too large.";
        isValid = false;
      } else if (!image.type.startsWith("image/")) {
        errors[index] =
          t("call_center.invalidImageType") || "Invalid image type.";
        isValid = false;
      }
    });
    setImageErrors(errors);
    return isValid;
  };

  // React validation for only patient, type, currency
  const validateForm = () => {
    let errors = {};
    let isValid = true;

    if (!formData.patient) {
      errors.patient = t("call_center.patientRequired", "Patient is required");
      isValid = false;
    }
    if (!formData.type) {
      errors.type = t("call_center.typeRequired", "Type is required.");
      isValid = false;
    }
    if (!formData.currency) {
      errors.currency = t(
        "call_center.currencyRequired",
        "Currency is required."
      );
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    if (!validateImages()) return;

    const submitData = new FormData();
    submitData.append("type", formData.type || "");
    if (formData.procedure_date)
      submitData.append("procedure_date", formData.procedure_date);
    submitData.append("procedure_day", formData.procedure_day);
    submitData.append("status", formData.status);
    submitData.append("period", formData.period);
    submitData.append("doctor", formData.doctor);
    submitData.append("total_cost", formData.total_cost);
    submitData.append("discount", formData.discount);
    submitData.append("advance", formData.advance);
    submitData.append("exchange_rate", formData.exchange_rate);
    submitData.append("currency", formData.currency);
    submitData.append("advance_currency", formData.advance_currency);
    submitData.append("zone", formData.zone);
    submitData.append("description", formData.description);
    submitData.append("patient", formData.patient?.id || "");
    submitData.append("next_visit_date", formData.next_visit_date);

    formData.lab.forEach((labId) => submitData.append("lab", labId));

    images.forEach((image) => {
      if (image) submitData.append("images", image);
    });

    handleAdd(submitData);
  };

  const handleOpenDialog = () => setOpen(!open);

  const handleDeleteChip = (valueToRemove) => {
    setFormData((prev) => ({
      ...prev,
      lab: prev.lab.filter((value) => value !== valueToRemove),
    }));
  };

  // Force update for autocomplete on empty search
  const forceUpdateAutocomplete = () => setForceUpdate((prev) => prev + 1);

  useEffect(() => {
    if (searchTerm === "") forceUpdateAutocomplete();
  }, [searchTerm]);

  return (
    <Grid container justifyContent="center">
      <Grid item xs={11.5} mt={2}>
        <Paper
          sx={{ padding: "20px", borderRadius: "10px", boxShadow: 3 }}
          component="form"
          onSubmit={handleSubmit}
        >
          {/* Go Back Icon and Title */}
          <Box display="flex" alignItems="center" mb={4}>
            <Tooltip title={t("pharmacy.goBack")} arrow>
              <IconButton
                onClick={() => navigate(-1)}
                aria-label={t("pharmacy.goBack")}
                sx={{ mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography
              variant="h5"
              gutterBottom
              fontWeight={"bold"}
              sx={{ borderBottom: "3px solid #007471", pb: 1.5 }}
            >
              {data
                ? t("call_center.editProcedure")
                : t("call_center.addNewProcedure")}
            </Typography>
          </Box>

          <Grid container spacing={3} mt={1}>
            {/* PATIENT */}
            <Grid item xs={12} md={4}>
              <FormLabel required>{t("call_center.patient")}</FormLabel>
              <Autocomplete
                options={allPatients}
                getOptionLabel={(option) =>
                  `${option.firstname} ${option.lastname || ""} - ${
                    option.phone || "N/A"
                  }`
                }
                inputValue={patientInputValue}
                onInputChange={handlePatientInputChange}
                onChange={handlePatientChange}
                value={formData.patient}
                ListboxProps={{ onScroll: handleScroll }}
                loading={isLoadingPatient}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={t("call_center.patient")}
                    error={!!formErrors.patient}
                    helperText={formErrors.patient}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {isLoadingPatient ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                filterOptions={(options, { inputValue }) => {
                  const input = inputValue.trim().toLowerCase();
                  return options.filter((option) => {
                    const nameMatch = `${option.firstname} ${
                      option.lastname || ""
                    }`
                      .toLowerCase()
                      .includes(input);
                    const phoneMatch = option.phone
                      ? option.phone.includes(input)
                      : false;
                    return nameMatch || phoneMatch;
                  });
                }}
              />
            </Grid>

            {/* TYPE */}
            <Grid item xs={12} md={4}>
              <FormLabel required>{t("call_center.type")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                error={!!formErrors.type}
                helperText={formErrors.type}
              >
                <MenuItem disabled value="">
                  {t("call_center.selectType")}
                </MenuItem>
                {isLoadingType && (
                  <Grid container justifyContent={"center"} padding={"10px"}>
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorType && (
                  <Typography color={"error"}>
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {typesData?.map((item, i) => (
                  <MenuItem value={item.id} key={i}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography>{item.name}</Typography>
                      {formData.type !== item.id && (
                        <DeleteForeverIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                          sx={{ color: "red" }}
                        />
                      )}
                    </Grid>
                  </MenuItem>
                ))}
                <MenuItem>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ textTransform: "capitalize" }}
                    onClick={() => {
                      setType("type");
                      handleOpenDialog();
                    }}
                  >
                    {t("call_center.addNewType")}
                  </Button>
                </MenuItem>
              </TextField>
            </Grid>

            {/* PROCEDURE DAY */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormLabel>{t("call_center.procedureDay")}</FormLabel>
                <Select
                  labelId="procedure_date-label"
                  id="procedure_date"
                  name="procedure_day"
                  value={formData.procedure_day || ""}
                  onChange={handleInputChange}
                  label={t("call_center.procedureDay")}
                >
                  <MenuItem value="monday">{t("call_center.monday")}</MenuItem>
                  <MenuItem value="tuesday">
                    {t("call_center.tuesday")}
                  </MenuItem>
                  <MenuItem value="wednesday">
                    {t("call_center.wednesday")}
                  </MenuItem>
                  <MenuItem value="thursday">
                    {t("call_center.thursday")}
                  </MenuItem>
                  <MenuItem value="friday">{t("call_center.friday")}</MenuItem>
                  <MenuItem value="saturday">
                    {t("call_center.saturday")}
                  </MenuItem>
                  <MenuItem value="sunday">{t("call_center.sunday")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* PROCEDURE DATE */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.procedureDate")}</FormLabel>
              <TextField
                fullWidth
                type="datetime-local"
                variant="outlined"
                name="procedure_date"
                value={formData.procedure_date}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* STATUS */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.status")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <MenuItem value={1}>{t("call_center.done")}</MenuItem>
                <MenuItem value={2}>{t("call_center.cancel")}</MenuItem>
                <MenuItem value={3}>{t("call_center.followUP")}</MenuItem>
                <MenuItem value={4}>{t("call_center.inProgress")}</MenuItem>
                <MenuItem value={5}>{t("call_center.dontWant")}</MenuItem>
                <MenuItem value={6}>{t("call_center.nextDate")}</MenuItem>
                <MenuItem value={7}>{t("call_center.pending")}</MenuItem>
                <MenuItem value={8}>{t("call_center.fixed")}</MenuItem>
                <MenuItem value={9}>{t("call_center.flexible")}</MenuItem>
                <MenuItem value={10}>{t("call_center.noAnswer")}</MenuItem>
                <MenuItem value={11}>{t("call_center.information")}</MenuItem>
                <MenuItem value={12}>{t("call_center.comingSoon")}</MenuItem>
                <MenuItem value={13}>{t("call_center.block")}</MenuItem>
              </TextField>
            </Grid>

            {/* PERIOD */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.period")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="period"
                value={formData.period}
                onChange={handleInputChange}
              >
                <MenuItem>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ textTransform: "capitalize" }}
                    onClick={() => {
                      setType("period");
                      handleOpenDialog();
                    }}
                  >
                    {t("call_center.addNewPeriod")}
                  </Button>
                </MenuItem>
                {isLoadingPeriod && (
                  <Grid container justifyContent={"center"} padding={"10px"}>
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorPeriod && (
                  <Typography color={"error"}>
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {periods?.map((item, i) => (
                  <MenuItem value={item.id} key={i}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography>{item.name}</Typography>
                      {formData.period !== item.id && (
                        <DeleteForeverIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePeriod(item.id);
                          }}
                          sx={{ color: "red" }}
                        />
                      )}
                    </Grid>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* DOCTOR */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.doctor")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="doctor"
                value={formData.doctor}
                onChange={handleInputChange}
              >
                {isLoadingUser && (
                  <Grid container justifyContent={"center"} padding={"10px"}>
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorUser && (
                  <Typography color={"error"}>
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {users?.map((item, i) => (
                  <MenuItem value={item.id} key={i}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography>
                        {item.first_name} {item.last_name}
                      </Typography>
                    </Grid>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* LAB */}
            <Grid item xs={12} md={4}>
              <FormLabel>{t("call_center.lab")}</FormLabel>
              <Select
                multiple
                fullWidth
                variant="outlined"
                placeholder="Lab"
                name="lab"
                value={formData.lab}
                onChange={handleInputChange}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={labs?.find((item) => item.id === value)?.name}
                        onDelete={(e) => {
                          e.stopPropagation();
                          handleDeleteChip(value);
                        }}
                      />
                    ))}
                  </Box>
                )}
              >
                <MenuItem>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ textTransform: "capitalize" }}
                    onClick={() => {
                      setType("lab");
                      handleOpenDialog();
                    }}
                  >
                    {t("call_center.addNewLab")}
                  </Button>
                </MenuItem>
                {isLoadingLab && (
                  <Grid container justifyContent={"center"} padding={"10px"}>
                    <CircularProgress />
                  </Grid>
                )}
                {isErrorLab && (
                  <Typography color={"error"}>
                    {t("call_center.errorField")}
                  </Typography>
                )}
                {labs?.map((item, i) => (
                  <MenuItem value={item.id} key={i}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography>{item.name}</Typography>
                      {formData.lab.indexOf(item.id) === -1 && (
                        <DeleteForeverIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLab(item.id);
                          }}
                          sx={{ color: "red" }}
                        />
                      )}
                    </Grid>
                  </MenuItem>
                ))}
              </Select>
              {formErrors.lab && (
                <Typography color="error">{formErrors.lab}</Typography>
              )}
            </Grid>

            {/* ZONE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.zone")}</FormLabel>
              <TextField
                fullWidth
                variant="outlined"
                name="zone"
                value={formData.zone}
                onChange={handleInputChange}
              />
            </Grid>

            {/* TOTAL PRICE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.totalPrice")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="total_cost"
                value={formData.total_cost}
                onChange={handleInputChange}
              />
            </Grid>

            {/* CURRENCY */}
            <Grid item xs={6} md={4}>
              <FormLabel required>{t("call_center.currency")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                error={!!formErrors.currency}
                helperText={formErrors.currency}
              >
                <MenuItem value="">{t("call_center.selectCurrency")}</MenuItem>
                <MenuItem value={1}>{t("call_center.afn")}</MenuItem>
                <MenuItem value={2}>{t("call_center.usd")}</MenuItem>
                <MenuItem value={3}>{t("call_center.eru")}</MenuItem>
              </TextField>
            </Grid>

            {/* ADVANCE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.advance")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="advance"
                value={formData.advance}
                onChange={handleInputChange}
              />
            </Grid>

            {/* ADVANCE CURRENCY */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.advanceCurrency")}</FormLabel>
              <TextField
                select
                fullWidth
                variant="outlined"
                name="advance_currency"
                value={formData.advance_currency}
                onChange={handleInputChange}
              >
                <MenuItem value={1}>{t("call_center.afn")}</MenuItem>
                <MenuItem value={2}>{t("call_center.usd")}</MenuItem>
                <MenuItem value={3}>{t("call_center.eru")}</MenuItem>
              </TextField>
            </Grid>

            {/* DISCOUNT */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.discount")}(%)</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="discount"
                value={formData.discount}
                onChange={handleInputChange}
              />
            </Grid>

            {/* EXCHANGE RATE */}
            <Grid item xs={6} md={4}>
              <FormLabel>
                {t("call_center.exchangeRate", "Exchanger Rate")}
              </FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="exchange_rate"
                value={formData.exchange_rate}
                onChange={handleInputChange}
              />
            </Grid>

            {/* NEXT VISIT DAYS */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.nextVisitDays")}</FormLabel>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                name="next_visit_days"
                value={formData.next_visit_days}
                onChange={handleInputChange}
              />
            </Grid>

            {/* NEXT VISIT DATE */}
            <Grid item xs={6} md={4}>
              <FormLabel>{t("call_center.nextVisitDate")}</FormLabel>
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                name="next_visit_date"
                value={formData.next_visit_date}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* DESCRIPTION */}
            <Grid item xs={6} md={4}>
              <FormLabel>
                {t("call_center.description", "Description")}
              </FormLabel>
              <TextField
                fullWidth
                variant="outlined"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* IMAGES SECTION */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  backgroundColor: "#417690",
                  color: "white",
                  padding: "10px",
                  borderRadius: "5px",
                }}
              >
                {t("call_center.uploadImages")}
              </Typography>
              {images.map((image, index) => (
                <React.Fragment key={index}>
                  <Box
                    sx={{
                      height: "40px",
                      width: "100%",
                      backgroundColor: "#F8F8F8",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.primary"
                      paddingLeft={1}
                    >
                      {t("call_center.image")} #{index + 1}
                    </Typography>
                    <IconButton
                      onClick={() => removeImageField(index)}
                      sx={{ color: "red" }}
                    >
                      <HighlightOffIcon />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      minHeight: "70px",
                      width: "100%",
                      backgroundColor: "white",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Grid container marginTop={1} marginBottom={1}>
                      <Grid item xl={3} lg={3} md={3} sm={3} xs={4}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          paddingLeft={1}
                          sx={{
                            fontSize: {
                              xl: "15px",
                              lg: "15px",
                              md: "14px",
                              sm: "12px",
                              xs: "10px",
                            },
                          }}
                        >
                          {t("call_center.imageFile")}:
                        </Typography>
                      </Grid>
                      <Grid item xl={9} lg={9} md={9} sm={9} xs={8}>
                        <Box
                          sx={{
                            border: "none",
                            outline: "none",
                            cursor: "pointer",
                            backgroundColor: "transparent",
                            "&::-webkit-file-upload-button": {
                              cursor: "pointer",
                              borderRadius: "4px",
                              padding: "5px 10px",
                              marginRight: 1,
                              border: "none",
                            },
                          }}
                          component={"input"}
                          type="file"
                          onChange={(e) => handleImageChange(e, index)}
                          accept="image/*"
                        />
                        <Typography fontSize="10px" color="error">
                          {imageErrors[index]}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </React.Fragment>
              ))}
              <Box
                sx={{
                  height: "40px",
                  width: "100%",
                  backgroundColor: "#F8F8F8",
                  color: "black",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Button
                  onClick={addImageField}
                  startIcon={<AddIcon sx={{ color: "green" }} />}
                  size="small"
                  sx={{
                    color: "#477893",
                    fontSize: "12px",
                    textTransform: "none",
                  }}
                >
                  {t("call_center.addImage")}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} textAlign="end">
              <LoadingButton
                loading={loading}
                variant="contained"
                color="success"
                type="submit"
                style={{ padding: "10px 20px" }}
              >
                {t("call_center.save")}
              </LoadingButton>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <ConfirmDialog />
      <LabConfirmDialog />
      <PeriodConfirmDialog />
      <Type open={open} onClose={handleOpenDialog} token={token} type={type} />
    </Grid>
  );
};

export default AddProcedure;
